import React, { createContext, useState, useContext, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

const WebsiteScrapeContext = createContext();

export const useWebsiteScrape = () => {
  return useContext(WebsiteScrapeContext);
};

// --- Scrape Progress Modal Component ---
function ScrapeProgressModal({ isOpen, onRequestClose, scrapeResults, isProcessing }) {
  const [isMinimized, setIsMinimized] = useState(false);

  const totalTasks = scrapeResults.length;
  const completedTasks = scrapeResults.filter(
    (r) => r.status === 'success' || r.status === 'error' || r.status === 'warning'
  ).length;
  const progressValue = totalTasks === 0 ? 0 : (completedTasks / totalTasks) * 100;
  const allDone = completedTasks === totalTasks && totalTasks > 0;

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1400,
          p: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 300,
          cursor: 'pointer',
        }}
        onClick={() => setIsMinimized(false)}
      >
        {!allDone && <CircularProgress size={18} />}
        {allDone && <CheckCircleIcon color="success" fontSize="small" />}
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          Website: {completedTasks}/{totalTasks} links
        </Typography>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}>
          <OpenInFullIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={allDone ? onRequestClose : undefined}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={!allDone}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Scraping & Training Progress</Typography>
        <Box>
          <IconButton size="small" onClick={() => setIsMinimized(true)} title="Minimize">
            <MinimizeIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {allDone ? 'All links processed' : `Processing ${completedTasks + 1} of ${totalTasks}...`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progressValue)}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progressValue} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
        <List dense sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {scrapeResults.map((result, index) => (
            <ListItem key={index} divider sx={{ py: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {result.status === 'pending' && <HourglassEmptyIcon color="disabled" />}
                {result.status === 'processing' && <CircularProgress size={20} />}
                {result.status === 'success' && <CheckCircleIcon color="success" />}
                {result.status === 'error' && <ErrorIcon color="error" />}
                {result.status === 'warning' && <WarningAmberIcon color="warning" />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {result.label}
                  </Typography>
                }
                secondary={
                  result.message && (
                    <Typography
                      variant="caption"
                      color={
                        result.status === 'error' ? 'error'
                        : result.status === 'warning' ? 'warning.main'
                        : 'success.main'
                      }
                    >
                      {result.message}
                    </Typography>
                  )
                }
              />
              <Chip
                size="small"
                label={
                  result.status === 'pending'
                    ? 'Pending'
                    : result.status === 'processing'
                    ? 'Processing...'
                    : result.status === 'success'
                    ? 'Done'
                    : result.status === 'warning'
                    ? 'Skipped'
                    : 'Failed'
                }
                color={
                  result.status === 'success'
                    ? 'success'
                    : result.status === 'error'
                    ? 'error'
                    : result.status === 'warning'
                    ? 'warning'
                    : 'default'
                }
                variant="outlined"
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onRequestClose} disabled={!allDone}>
          {allDone ? 'Close' : 'Processing...'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const WebsiteScrapeProvider = ({ children }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [scrapeResults, setScrapeResults] = useState([]);
  const [progressOpen, setProgressOpen] = useState(false);
  const onCompleteRef = useRef(null);
  const isProcessingRef = useRef(false);

  const registerOnComplete = useCallback((callback) => {
    onCompleteRef.current = callback;
  }, []);

  const unregisterOnComplete = useCallback(() => {
    onCompleteRef.current = null;
  }, []);

  const closeProgress = useCallback(() => {
    setProgressOpen(false);
    setScrapeResults([]);
  }, []);

  // --- Helper: check subscription web page limit ---
  const getWebPageLimit = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/subscriptions/current');
      const sub = response.data;
      return {
        limit: sub?.remaining_web_pages_limit || null,
        plan: sub?.plan?.display_name || sub?.plan?.name || 'Free',
      };
    } catch {
      return { limit: null, plan: 'Unknown' };
    }
  }, []);

  // --- Add single website link (auto scrape+train via backend) ---
  const startWebsiteAdd = useCallback(async (url, chatbotId) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);

    const results = [{ label: url, status: 'processing', message: 'Adding, scraping & training...' }];
    setScrapeResults(results);
    setProgressOpen(true);

    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    try {
      const response = await axiosInstance.post('/add-website-link', { website_link: url }, { params });
      results[0].status = 'success';
      results[0].message = response.data.message || 'Added, scraped & trained successfully.';
    } catch (err) {
      results[0].status = 'error';
      results[0].message = err.response?.data?.message || err.message || 'Failed to add website link.';
    }

    setScrapeResults([...results]);
    isProcessingRef.current = false;
    setIsProcessing(false);

    if (onCompleteRef.current) onCompleteRef.current();
  }, []);

  // --- Add bulk website links (each auto scrape+train via backend) ---
  const startBulkWebsiteAdd = useCallback(async (links, chatbotId) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);

    const results = links.map((link) => ({
      label: link,
      status: 'pending',
      message: '',
    }));
    setScrapeResults(results);
    setProgressOpen(true);

    // Check subscription limit
    const { limit, plan } = await getWebPageLimit();
    if (limit !== null && links.length > limit) {
      // Mark links within limit as pending, rest as warning
      for (let i = limit; i < links.length; i++) {
        results[i].status = 'warning';
        results[i].message = `Limit exceeded. Your "${plan}" plan allows up to ${limit} website links. Please upgrade your plan.`;
      }
      setScrapeResults([...results]);
    }

    const params = chatbotId ? { chatbot_id: chatbotId } : {};
    const processableCount = limit !== null ? Math.min(links.length, limit) : links.length;

    for (let i = 0; i < processableCount; i++) {
      results[i].status = 'processing';
      results[i].message = 'Adding, scraping & training...';
      setScrapeResults([...results]);

      try {
        const response = await axiosInstance.post('/add-website-link', { website_link: links[i] }, { params });
        results[i].status = 'success';
        results[i].message = response.data.message || 'Added, scraped & trained successfully.';
      } catch (err) {
        results[i].status = 'error';
        results[i].message = err.response?.data?.message || err.message || 'Failed.';
      }

      setScrapeResults([...results]);
    }

    isProcessingRef.current = false;
    setIsProcessing(false);

    if (onCompleteRef.current) onCompleteRef.current();
  }, [getWebPageLimit]);

  // --- Add sitemap/homepage → then scrape all unscraped links ---
  const startSitemapAdd = useCallback(async (sitemapUrl, chatbotId, type = 'Sitemap') => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);

    // Step 1: Add sitemap
    const results = [{ label: `${type}: ${sitemapUrl}`, status: 'processing', message: `Adding ${type.toLowerCase()}...` }];
    setScrapeResults(results);
    setProgressOpen(true);

    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    try {
      await axiosInstance.post('/add-sitemap', { sitemap: sitemapUrl }, { params });
      results[0].status = 'success';
      results[0].message = `${type} added successfully. Fetching links to scrape...`;
      setScrapeResults([...results]);
    } catch (err) {
      results[0].status = 'error';
      results[0].message = err.response?.data?.message || err.message || `Failed to add ${type.toLowerCase()}.`;
      setScrapeResults([...results]);
      isProcessingRef.current = false;
      setIsProcessing(false);
      if (onCompleteRef.current) onCompleteRef.current();
      return;
    }

    // Step 2: Fetch all unscraped links
    let unscrapedLinks = [];
    try {
      const response = await axiosInstance.get('/get-unscrape-website-links', {
        params: { ...params, page: 1, limit: 9999 },
      });
      // Response: { success, data: JSON string of { data: [...], total, page, limit } }
      const parsed = typeof response.data.data === 'string'
        ? JSON.parse(response.data.data)
        : response.data.data;
      unscrapedLinks = parsed?.data || [];
    } catch (err) {
      results[0].message = `${type} added. Could not fetch links for scraping.`;
      setScrapeResults([...results]);
      isProcessingRef.current = false;
      setIsProcessing(false);
      if (onCompleteRef.current) onCompleteRef.current();
      return;
    }

    if (unscrapedLinks.length === 0) {
      results[0].message = `${type} added. No new links found to scrape.`;
      setScrapeResults([...results]);
      isProcessingRef.current = false;
      setIsProcessing(false);
      if (onCompleteRef.current) onCompleteRef.current();
      return;
    }

    // Step 3: Check subscription limit
    const { limit, plan } = await getWebPageLimit();

    const linkResults = unscrapedLinks.map((link) => ({
      label: link.website_link,
      status: 'pending',
      message: '',
    }));

    // Mark links that exceed the limit as warning/skipped
    const processableCount = limit !== null ? Math.min(unscrapedLinks.length, limit) : unscrapedLinks.length;

    if (limit !== null && unscrapedLinks.length > limit) {
      for (let i = limit; i < unscrapedLinks.length; i++) {
        linkResults[i].status = 'warning';
        linkResults[i].message = `Limit exceeded. Your "${plan}" plan allows up to ${limit} links. Please upgrade your plan.`;
      }
    }

    setScrapeResults([...results, ...linkResults]);

    // Step 4: Scrape each link within limit
    for (let i = 0; i < processableCount; i++) {
      const link = unscrapedLinks[i];
      linkResults[i].status = 'processing';
      linkResults[i].message = 'Scraping & training...';
      setScrapeResults([...results, ...linkResults]);

      try {
        const scrapeResponse = await axiosInstance.post(
          '/add-scrape-data',
          { data: [[link.id], [link.website_link]] },
          { params }
        );
        const responseData = typeof scrapeResponse.data.data === 'string'
          ? JSON.parse(scrapeResponse.data.data)
          : scrapeResponse.data.data;

        if (responseData?.scrape_data?.[0]?.page_content) {
          linkResults[i].status = 'success';
          linkResults[i].message = 'Scraped & trained successfully.';
        } else {
          linkResults[i].status = 'error';
          linkResults[i].message = responseData?.scrape_data?.[0]?.error || 'No content returned.';
        }
      } catch (err) {
        linkResults[i].status = 'error';
        linkResults[i].message = err.response?.data?.message || err.message || 'Scraping failed.';
      }

      setScrapeResults([...results, ...linkResults]);
    }

    isProcessingRef.current = false;
    setIsProcessing(false);

    if (onCompleteRef.current) onCompleteRef.current();
  }, [getWebPageLimit]);

  const value = {
    isProcessing,
    scrapeResults,
    progressOpen,
    startWebsiteAdd,
    startBulkWebsiteAdd,
    startSitemapAdd,
    closeProgress,
    registerOnComplete,
    unregisterOnComplete,
  };

  return (
    <WebsiteScrapeContext.Provider value={value}>
      {children}
      <ScrapeProgressModal
        isOpen={progressOpen}
        onRequestClose={closeProgress}
        scrapeResults={scrapeResults}
        isProcessing={isProcessing}
      />
    </WebsiteScrapeContext.Provider>
  );
};
